import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import { visuallyHidden } from "../visuallyHidden";

/**
 * Lightweight routed-page wrapper. The application shell owns the responsive
 * content width and gutters; this component only supplies a consistent vertical
 * rhythm and an optional route heading.
 *
 * `title` is the page's h1, rendered as a compact heading so task and detail
 * pages keep their orientation. Tab-root pages (the bottom-nav destinations)
 * pass `hideTitle`: the active tab already says where you are, so the h1 stays
 * for screen readers only.
 */
export function Page({
  children,
  title,
  hideTitle = false,
  narrow = false,
  back,
}: {
  children: ReactNode;
  title?: string;
  hideTitle?: boolean;
  /** Keep content in the approved 720px reading column on wide screens. */
  narrow?: boolean;
  /** A sub-page's way back to its parent, shown above the title. */
  back?: { to: string; label: string };
}) {
  return (
    <Box sx={{ width: "100%", minWidth: 0, ...(narrow ? { maxWidth: 720 } : {}) }}>
      <Stack spacing={2.5} useFlexGap sx={{ width: "100%", minWidth: 0 }}>
        {back ? (
          <Button
            component={RouterLink}
            to={back.to}
            size="small"
            color="inherit"
            startIcon={<ArrowBackRounded />}
            sx={{ alignSelf: "flex-start", ml: -1, mb: -1.5, color: "text.secondary" }}
          >
            {back.label}
          </Button>
        ) : null}
        {title ? (
          <Typography
            component="h1"
            sx={hideTitle ? visuallyHidden : { fontSize: "1.375rem", fontWeight: 700, overflowWrap: "anywhere" }}
          >
            {title}
          </Typography>
        ) : null}
        {children}
      </Stack>
    </Box>
  );
}
